/**
 * Navigation — Shared header, breadcrumb, footer for all public pages
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { el } from '/js/ui/dom.js';

const API_BASE = '/api/v1';

// Lightweight i18n fallback — uses loaded translations if available, otherwise defaults
let _translations = null;
async function _ensureTranslations() {
    if (_translations) return;
    try {
        const locale = localStorage.getItem('boomfection_locale') || 'de';
        const res = await fetch(`/locales/${locale}.json`);
        if (res.ok) _translations = await res.json();
    } catch {
        /* expected — fetch failed (offline) or locale JSON unavailable; use fallbacks */
    }
    if (!_translations) _translations = {};
}
function _t(key, fallback) {
    if (!_translations) return fallback;
    const keys = key.split('.');
    let val = _translations;
    for (const k of keys) {
        if (val && typeof val === 'object' && k in val) val = val[k];
        else return fallback;
    }
    return typeof val === 'string' ? val : fallback;
}

/** @type {Object|null} */
let currentUser = null;

/** @type {Function[]} */
const authChangeListeners = [];

/** @type {boolean} */
let initialized = false;

/**
 * Navigation module — renders auth header + breadcrumbs + footer
 */
export const Navigation = {
    /**
     * Initialize navigation on the page
     * @param {Object} [options]
     * @param {boolean} [options.showNav=true]
     * @param {boolean} [options.showFooter=true]
     * @param {string[]} [options.breadcrumb] - e.g. ['Startseite', 'Account']
     */
    async init(options = {}) {
        if (initialized) return;
        initialized = true;

        const { showNav = true, showFooter = true, breadcrumb = null } = options;

        await _ensureTranslations();

        // Set html lang attribute based on locale
        const locale = localStorage.getItem('boomfection_locale') || 'de';
        document.documentElement.lang = locale;

        if (showNav) {
            this._renderHeader();
            if (breadcrumb && breadcrumb.length > 0) {
                this._renderBreadcrumb(breadcrumb);
            }
            this._bindEvents();
        }

        if (showFooter) {
            this._renderFooter();
        }

        // Check for magic link token in URL hash (/#token=...)
        this._checkMagicLinkToken();

        await this._restoreAuthState();

        // Check for impersonation token in URL hash
        this._checkImpersonation();
    },

    /**
     * Get the current user object
     * @returns {Object|null}
     */
    getUser() {
        return currentUser;
    },

    /**
     * Register a callback for auth state changes
     * @param {Function} callback - receives (user|null)
     */
    onAuthChange(callback) {
        authChangeListeners.push(callback);
        if (currentUser) callback(currentUser);
    },

    /**
     * Make an authenticated API call with auto-refresh
     * @param {string} url
     * @param {Object} [options]
     * @returns {Promise<Response|null>}
     */
    async authFetch(url, options = {}) {
        let token = _getAccessToken();
        if (!token) {
            token = await _refreshToken();
        }
        if (!token) return null;

        options.headers = {
            ...options.headers,
            Authorization: `Bearer ${token}`
        };
        if (!options.headers['Content-Type'] && !(options.body instanceof FormData)) {
            options.headers['Content-Type'] = 'application/json';
        }
        options.credentials = 'include';

        let res = await fetch(url, options);

        if (res.status === 401) {
            token = await _refreshToken();
            if (!token) return null;
            options.headers['Authorization'] = `Bearer ${token}`;
            res = await fetch(url, options);
        }

        return res;
    },

    /**
     * Programmatically set logged-in user (used by landing.js login flow)
     * @param {Object} user
     */
    setUser(user) {
        _setLoggedInUser(user);
    },

    /**
     * Programmatically clear user (e.g. on logout)
     */
    clearUser() {
        _clearLoggedInUser();
    },

    // =========================================================================
    // PRIVATE RENDER METHODS
    // =========================================================================

    _renderHeader() {
        let container = document.getElementById('site-header');
        if (!container) {
            container = el('div', { id: 'site-header' });
            document.body.prepend(container);
        }
        container.textContent = '';

        const header = el('div', { className: 'auth-header', id: 'auth-header' });

        // Nav links
        const nav = el('nav', { className: 'auth-nav' }, [
            el('a', { href: '/', className: 'auth-nav-link', textContent: _t('nav.home', 'Startseite') }),
            el('a', {
                href: '/multiplayer',
                className: 'auth-nav-link',
                textContent: _t('nav.multiplayer', 'Multiplayer')
            })
        ]);
        header.appendChild(nav);

        // Language switcher
        const currentLang = localStorage.getItem('boomfection_locale') || 'de';
        const langToggle = el('button', {
            className: 'auth-lang-toggle',
            id: 'btn-lang-toggle',
            textContent: currentLang.toUpperCase(),
            title: currentLang === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'
        });
        header.appendChild(langToggle);

        // Logged out state
        const loggedOut = el('div', { id: 'auth-logged-out', className: 'auth-state' }, [
            el('button', { className: 'auth-link', id: 'btn-login', textContent: _t('nav.login', 'Anmelden') }),
            el('span', { className: 'auth-divider', textContent: '\u00B7' }),
            el('button', {
                className: 'auth-link',
                id: 'btn-register',
                textContent: _t('nav.register', 'Registrieren')
            })
        ]);
        header.appendChild(loggedOut);

        // Logged in state
        const avatarEl = el('div', { className: 'auth-avatar', id: 'auth-avatar' });
        const usernameEl = el('span', { className: 'auth-username', id: 'auth-username' });
        const arrowEl = el('span', { className: 'auth-dropdown-arrow', textContent: '\u25BE' });

        const userInfo = el('div', { className: 'auth-user-info', id: 'auth-user-dropdown' }, [
            avatarEl,
            usernameEl,
            arrowEl
        ]);

        const dropdown = el('div', { className: 'auth-dropdown hidden', id: 'auth-dropdown-menu' }, [
            el('a', {
                href: '/account',
                id: 'auth-btn-profile',
                className: 'auth-dropdown-item',
                textContent: _t('nav.profile', 'Mein Profil')
            }),
            el('a', {
                href: '/admin',
                id: 'auth-btn-admin',
                className: 'auth-dropdown-item hidden',
                textContent: _t('nav.admin', 'Admin')
            }),
            el('button', {
                className: 'auth-dropdown-item auth-logout',
                id: 'auth-btn-logout',
                textContent: _t('nav.logout', 'Abmelden')
            })
        ]);

        const loggedIn = el('div', { id: 'auth-logged-in', className: 'auth-state hidden' }, [userInfo, dropdown]);
        header.appendChild(loggedIn);

        container.appendChild(header);
    },

    _renderBreadcrumb(items) {
        const container = document.getElementById('site-header');
        if (!container) return;

        const ol = el('ol', { className: 'breadcrumb-list' });

        items.forEach((item, i) => {
            const li = el('li', { className: 'breadcrumb-item' });
            if (i === items.length - 1) {
                li.setAttribute('aria-current', 'page');
                li.textContent = item;
            } else {
                const link = el('a', { href: i === 0 ? '/' : '#', textContent: item });
                li.appendChild(link);
            }
            ol.appendChild(li);
        });

        const bc = el('nav', { className: 'breadcrumb', 'aria-label': 'Breadcrumb' }, [ol]);
        container.appendChild(bc);
    },

    _renderFooter() {
        let container = document.getElementById('site-footer');
        if (!container) {
            container = el('footer', { id: 'site-footer' });
            document.body.appendChild(container);
        }

        container.className = 'site-footer';
        container.textContent = '';
        container.appendChild(el('a', { href: '/impressum', textContent: _t('nav.impressum', 'Impressum') }));
        container.appendChild(el('span', { className: 'footer-divider', textContent: '\u00B7' }));
        container.appendChild(el('a', { href: '/datenschutz', textContent: _t('nav.datenschutz', 'Datenschutz') }));
        container.appendChild(el('span', { className: 'footer-divider', textContent: '\u00B7' }));
        container.appendChild(el('span', {}, ['\u00A9 reDim GmbH']));
    },

    _bindEvents() {
        // Language toggle
        document.getElementById('btn-lang-toggle')?.addEventListener('click', () => {
            const current = localStorage.getItem('boomfection_locale') || 'de';
            const next = current === 'de' ? 'en' : 'de';
            localStorage.setItem('boomfection_locale', next);
            window.location.reload();
        });

        // Login/Register — dispatch custom events for page-specific handling
        document.getElementById('btn-login')?.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent('nav:login'));
        });
        document.getElementById('btn-register')?.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent('nav:register'));
        });

        // Logout
        document.getElementById('auth-btn-logout')?.addEventListener('click', async () => {
            try {
                await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
            } catch {
                /* fire-and-forget — logout clears local state regardless of server response */
            }
            localStorage.removeItem('accessToken');
            _clearLoggedInUser();
            if (window.location.pathname.startsWith('/account') || window.location.pathname.startsWith('/player/')) {
                window.location.href = '/';
            }
        });

        // Dropdown toggle
        document.getElementById('auth-user-dropdown')?.addEventListener('click', () => {
            document.getElementById('auth-dropdown-menu')?.classList.toggle('hidden');
        });

        // Profile link
        document.getElementById('auth-btn-profile')?.addEventListener('click', (e) => {
            e.preventDefault();
            if (currentUser) {
                window.location.href = `/player/${encodeURIComponent(currentUser.username)}`;
            }
        });

        // Close dropdown on outside click
        document.addEventListener('click', (e) => {
            const dropdown = document.getElementById('auth-dropdown-menu');
            const trigger = document.getElementById('auth-user-dropdown');
            if (dropdown && trigger && !trigger.contains(e.target) && !dropdown.contains(e.target)) {
                dropdown.classList.add('hidden');
            }
        });
    },

    /**
     * Check for magic link token in URL hash (/#token=...)
     * Saves the access token from magic link redirect and cleans the hash
     */
    _checkMagicLinkToken() {
        const hash = window.location.hash;
        if (!hash.startsWith('#token=')) return;

        const token = decodeURIComponent(hash.substring('#token='.length));
        if (!token || !/^[\w-]+\.[\w-]+\.[\w-]+$/.test(token)) return;

        localStorage.setItem('accessToken', token);
        // Clear hash without triggering hashchange
        history.replaceState(null, '', window.location.pathname + window.location.search);
    },

    /**
     * Check for impersonation token in URL hash
     */
    _checkImpersonation() {
        const hash = window.location.hash;
        if (!hash.startsWith('#impersonate=')) return;

        const token = hash.substring('#impersonate='.length);
        // Basic JWT format validation (header.payload.signature)
        if (!token || !/^[\w-]+\.[\w-]+\.[\w-]+$/.test(token)) return;

        // Payload prüfen: muss impersonated=true enthalten (Security: verhindert Token-Injection)
        try {
            const payload = JSON.parse(window.atob(token.split('.')[1]));
            if (!payload.impersonated) return;
        } catch { return; }

        // Store the impersonation token
        localStorage.setItem('accessToken', token);
        // Clear hash without triggering hashchange
        history.replaceState(null, '', window.location.pathname + window.location.search);

        // Show impersonation banner
        const banner = el('div', { className: 'impersonation-banner', id: 'impersonation-banner' }, [
            `\uD83D\uDC41 ${  _t('nav.impersonationBanner', 'Du siehst die Seite als Spieler (Read-Only) \u2014 ')}`,
            el('a', { href: '#', textContent: _t('nav.backToAdmin', 'Zur\u00FCck zum Admin') })
        ]);
        banner.querySelector('a').addEventListener('click', (e) => {
            e.preventDefault();
            localStorage.removeItem('accessToken');
            banner.remove();
            window.close();
            // Fallback if window.close() is blocked by browser
            setTimeout(() => {
                window.location.href = '/admin';
            }, 300);
        });
        document.body.prepend(banner);

        // Disable all forms and buttons
        setTimeout(() => {
            document.querySelectorAll('form, button, input, textarea, select').forEach((node) => {
                if (!node.closest('#impersonation-banner')) {
                    node.setAttribute('disabled', 'true');
                    node.style.pointerEvents = 'none';
                }
            });
        }, 500);
    },

    /**
     * Restore auth state from localStorage + server verification
     */
    async _restoreAuthState() {
        const saved = localStorage.getItem('boomfection_user');
        if (saved) {
            try {
                currentUser = JSON.parse(saved);
                _updateAuthUI();
            } catch {
                /* expected — corrupted localStorage entry; remove and re-fetch */
                localStorage.removeItem('boomfection_user');
            }
        }

        try {
            const token = localStorage.getItem('accessToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include', headers });
            if (res.ok) {
                const data = await res.json();
                if (data && data.user) {
                    _setLoggedInUser(data.user);
                    return;
                }
            }

            if (currentUser) {
                const token = await _refreshToken();
                if (token) {
                    const meRes = await fetch(`${API_BASE}/auth/me`, {
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (meRes.ok) {
                        const data = await meRes.json();
                        if (data && data.user) {
                            _setLoggedInUser(data.user);
                            return;
                        }
                    }
                }
                _clearLoggedInUser();
            }
        } catch {
            // Offline or server error — keep cached state
        }
    }
};

// =========================================================================
// PRIVATE HELPERS
// =========================================================================

function _getAccessToken() {
    return localStorage.getItem('accessToken') || null;
}

async function _refreshToken() {
    try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (data.accessToken) {
            localStorage.setItem('accessToken', data.accessToken);
            return data.accessToken;
        }
        return null;
    } catch {
        /* expected — network error or server unavailable during token refresh */
        return null;
    }
}

function _setLoggedInUser(user) {
    currentUser = user;
    localStorage.setItem('boomfection_user', JSON.stringify(user));
    _updateAuthUI();
    for (const cb of authChangeListeners) {
        try {
            cb(user);
        } catch {
            /* ignore — individual auth-change listener errors must not break other listeners */
        }
    }
}

function _clearLoggedInUser() {
    currentUser = null;
    localStorage.removeItem('boomfection_user');
    _updateAuthUI();
    for (const cb of authChangeListeners) {
        try {
            cb(null);
        } catch {
            /* ignore — individual auth-change listener errors must not break other listeners */
        }
    }
}

function _updateAuthUI() {
    const loggedOut = document.getElementById('auth-logged-out');
    const loggedIn = document.getElementById('auth-logged-in');
    if (!loggedOut || !loggedIn) return;

    if (currentUser) {
        loggedOut.classList.add('hidden');
        loggedIn.classList.remove('hidden');

        const usernameEl = document.getElementById('auth-username');
        if (usernameEl) {
            usernameEl.textContent = currentUser.username || currentUser.displayName || 'User';
        }

        const avatarEl = document.getElementById('auth-avatar');
        if (avatarEl) {
            if (currentUser.avatarUrl) {
                avatarEl.style.backgroundImage = `url(${currentUser.avatarUrl})`;
                avatarEl.textContent = '';
            } else {
                avatarEl.style.backgroundImage = '';
                avatarEl.textContent = (currentUser.username || 'U').charAt(0).toUpperCase();
            }
        }

        // Show/hide admin link based on role
        const adminLink = document.getElementById('auth-btn-admin');
        if (adminLink) {
            const adminRoles = ['moderator', 'admin', 'superadmin'];
            adminLink.classList.toggle('hidden', !adminRoles.includes(currentUser.role));
        }
    } else {
        loggedOut.classList.remove('hidden');
        loggedIn.classList.add('hidden');
    }
}
